import {
    Avatar,
    createStyles,
    Grid,
    SwipeableDrawer,
    Theme,
    Typography,
    withStyles,
    WithStyles,
} from "@material-ui/core";
import classNames from "classnames";
import React from "react";
import { getGlobalUserId } from "shared/src/framework/network/headers";
import { Id } from "shared/src/models/framework/db";
import { Mode, PollComment } from "shared/src/models/poll";
import { addComment, getComments } from "shared/src/network/requests/poll/poll-req";
import { PollCard } from "shared/src/network/requests/poll/poll-req-models";
import CommentInput from "./CommentInput";

const styles = (theme: Theme) =>
    createStyles({
        drawerPaper: {
            padding: theme.spacing(2),
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        },
        avatar: {
            height: "40px",
            width: "40px",
        },
    });

type Props = WithStyles<typeof styles> & {
    show: boolean;
    poll: Id<PollCard>;
    mode?: Mode;
    onClose?: (updatedComments: Array<PollComment>) => void;
    onCommentSent?: () => void;
};

type State = {
    comments: Array<Id<PollComment>>;
    // comments: Array<PollComment>;
    setFixPosition?: boolean /* We need "fixed" position of comment box in drawer so that 
    comment list can scroll without affecting input box position. But iOS Safari has issue
    with "fixed" position when input is focused.
    refer: https://stackoverflow.com/questions/14492613/ios-ipad-fixed-position-breaks-when-keyboard-is-opened
    As a fix, appling "absolute" position to input box and replacing it with "fixed" position when
    slide animation is completed for drawer open (entered) and changing back to "absolute" when
    slide animation is completed for drawer close (exited). */;
};

class Component extends React.Component<Props, State> {
    users: string[];
    currentUser?: string;
    constructor(props: Props) {
        super(props);
        this.state = { comments: [] };
        this.users = [];
        this.currentUser = getGlobalUserId();
    }

    getNewComment = async () => {
        const comments = await getComments({ pollId: this.props.poll.id });
        comments.forEach((each) => {
            if (!this.users.includes(each.userId)) {
                this.users.push(each.userId);
            }
        });
        this.setState({ comments });
    };
    componentDidUpdate = async (prevProps: Props) => {
        const { show } = this.props;
        if (!prevProps.show && show) {
            this.getNewComment();
        }
    };

    handleDrawerOpen = () => {};

    handleDrawerClose = () => {
        this.props.onClose?.(this.state.comments);
    };

    handleDrawerSlideEntered = () => {
        this.setState({ setFixPosition: true });
    };

    handleDrawerSlideExited = () => {
        this.setState({ setFixPosition: false });
    };

    handleCommentSend = (comment: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            if (!this.currentUser) return reject("Current user is null");
            const { poll, onCommentSent } = this.props;
            try {
                await addComment({
                    pollId: poll.id,
                    userId: this.currentUser,
                    message: comment,
                });
                this.getNewComment();
                resolve();
                onCommentSent?.();
            } catch {
                reject("Adding comment failed.");
            }
        });
    };

    render = () => {
        const { classes, show, poll, mode } = this.props;
        const { setFixPosition, comments } = this.state;
        return (
            <SwipeableDrawer
                classes={{ paper: classNames(classes.drawerPaper) }}
                open={show}
                anchor="bottom"
                disableDiscovery
                disableSwipeToOpen
                onOpen={this.handleDrawerOpen}
                onClose={this.handleDrawerClose}
                SlideProps={{
                    onEntered: this.handleDrawerSlideEntered,
                    onExited: this.handleDrawerSlideExited,
                }}>
                <Grid container direction="column" sx={{ paddingBottom: "40px" }}>
                    {comments.length > 0 ? (
                        comments.map((each, index) => {
                            return (
                                <Grid key={index} item sx={{ my: 1 }}>
                                    <Grid container wrap="nowrap">
                                        <Grid item>
                                            {each.image ? (
                                                <Avatar
                                                    sx={{ width: 40, height: 40 }}
                                                    src={`${process.env.REACT_APP_MEDIA_HOST}${each.image}`}
                                                />
                                            ) : (
                                                <Avatar />
                                            )}
                                        </Grid>
                                        <Grid item sx={{ pl: 1 }}>
                                            <Typography sx={{ fontWeight: "bold" }}>
                                                {this.currentUser === each.userId
                                                    ? "You"
                                                    : each.name
                                                    ? each.name
                                                    : `Anonymous ${
                                                          this.users.indexOf(each.userId) + 1
                                                      }`}
                                            </Typography>
                                            <Typography>{each.message}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })
                    ) : (
                        <Grid item sx={{ my: 1 }}>
                            <Grid container wrap="nowrap">
                                <Grid item sx={{ pl: 1 }}>
                                    <Typography>Be the first to share what you think!</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    <Grid
                        item
                        sx={{
                            position: setFixPosition ? "fixed" : "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: "white",
                            padding: "8px 16px 16px",
                        }}>
                        <CommentInput
                            poll={poll}
                            mode={mode}
                            onCommentSend={this.handleCommentSend}
                            autoFocus
                        />
                    </Grid>
                </Grid>
            </SwipeableDrawer>
        );
    };
}

const CommentDrawer = withStyles(styles)(Component);

export default CommentDrawer;
